import { useEffect, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import { Card, CardContent } from '@src/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@src/components/ui/tabs';

const IMPACT_GROUPS = [
  { key: 'entities', label: 'Entities' },
  { key: 'locations', label: 'Locations' },
  { key: 'applications', label: 'Applications' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'data', label: 'Data' },
];

export function ScenarioCarousel({ scenario }) {
  const { org, scenarios } = scenario;
  const [activeId, setActiveId] = useState(scenarios[0].id);
  const shouldReduceMotion = useReducedMotion();
  const tabRefs = useRef({});
  const stripRef = useRef(null);
  const isFirstRender = useRef(true);

  const activeIndex = scenarios.findIndex((item) => item.id === activeId);

  const goTo = (index) => {
    const wrapped = (index + scenarios.length) % scenarios.length;
    setActiveId(scenarios[wrapped].id);
  };

  /*
   * On narrow viewports the tab strip scrolls horizontally, so a scenario
   * reached by chevron or dot can sit off-screen. Pull it back into view by
   * scrolling the strip itself.
   *
   * Deliberately NOT scrollIntoView: that scrolls every scrollable ancestor,
   * including the page. Because this section sits below the fold, it dragged
   * the whole page down to the carousel the moment an industry page loaded.
   * Scrolling the strip's own scrollLeft cannot move the page at all.
   *
   * The first render is skipped for the same reason — on mount there has been
   * no navigation to follow, so there is nothing to bring into view.
   */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const strip = stripRef.current;
    const tab = tabRefs.current[activeId];

    if (!strip || !tab) {
      return;
    }

    strip.scrollTo({
      left: Math.max(0, tab.offsetLeft - (strip.clientWidth - tab.clientWidth) / 2),
      behavior: shouldReduceMotion ? 'auto' : 'smooth',
    });
  }, [activeId, shouldReduceMotion]);

  return (
    <section className="py-20 bg-slate-50 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Scenario Walkthrough</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-3">{org.subtitle}</p>

          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {org.profile.map((entry) => {
              const ProfileIcon = entry.icon;

              return (
                <div
                  key={entry.text}
                  className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 shadow-sm"
                >
                  <ProfileIcon className="w-4 h-4 text-blue-600 shrink-0" />
                  <span className="text-sm font-medium text-slate-800">{entry.text}</span>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-400 max-w-3xl mx-auto mt-4">
            {`${org.name} — ${org.framingNote}`}
          </p>
        </div>

        <Tabs value={activeId} onValueChange={setActiveId}>
          <div className="flex items-center gap-3 mb-8">
            <button
              type="button"
              aria-label="Previous scenario"
              className="shrink-0 rounded-full border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              onClick={() => goTo(activeIndex - 1)}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* The scroll container and the tab list are separate elements on
                purpose. Centring a list that overflows its own scroll box makes
                the leading tabs unreachable — no amount of scrolling brings them
                back. `w-max` plus `mx-auto` centres the list only while it fits,
                and left-aligns it the moment it does not. */}
            <div ref={stripRef} className="flex-1 overflow-x-auto">
              <TabsList className="h-auto w-max mx-auto bg-transparent p-0 gap-2">
                {scenarios.map((item) => (
                  <TabsTrigger
                    ref={(node) => {
                      tabRefs.current[item.id] = node;
                    }}
                    key={item.id}
                    value={item.id}
                    className="flex-none whitespace-nowrap rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs data-[state=active]:border-blue-500 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                  >
                    <span className="font-semibold mr-1.5">{item.number}</span>
                    {item.title}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <button
              type="button"
              aria-label="Next scenario"
              className="shrink-0 rounded-full border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              onClick={() => goTo(activeIndex + 1)}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {scenarios.map((item) => {
            const ScenarioIcon = item.icon;

            return (
              <TabsContent key={item.id} value={item.id}>
                <motion.div
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35 }}
                  initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: 24 }}
                >
                  <Card className="border-slate-200">
                    <CardContent className="p-6 lg:p-10">
                      <div className="grid lg:grid-cols-2 gap-10">
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                              <ScenarioIcon className="w-5 h-5 text-white" />
                            </div>

                            <div>
                              <p className="text-xs uppercase tracking-wider text-slate-500">The situation</p>
                              <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                            </div>
                          </div>

                          <p className="text-slate-500 italic mb-4">{item.tagline}</p>
                          <p className="text-gray-700 leading-relaxed mb-6">{item.situation.narrative}</p>

                          <div className="mb-6">
                            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Who is involved</p>

                            <ul className="space-y-1">
                              {item.situation.cast.map((person) => (
                                <li key={person.name} className="text-sm text-gray-700">
                                  <span className="font-medium">{person.name}</span>
                                  <span className="text-gray-500">{` — ${person.role}`}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="mb-6">
                            <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Scenario timing</p>

                            <ul className="space-y-1">
                              {item.timing.map((entry) => (
                                <li className="flex gap-3 text-sm" key={`${entry.at}-${entry.label}`}>
                                  <span className="font-mono font-semibold text-blue-700 shrink-0 w-32">{entry.at}</span>
                                  <span className="text-gray-700">{entry.label}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {item.frameworks.map((framework) => (
                              <span
                                key={framework}
                                className="px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-xs font-medium"
                              >
                                {framework}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">How BOSSS handles it</p>

                          <ol className="space-y-4 mb-8">
                            {item.response.map((step, index) => (
                              <li key={`${step.module}-${index}`} className="border-l-2 border-blue-200 pl-4">
                                <a
                                  href={step.href}
                                  className="inline-block px-2.5 py-0.5 rounded-md bg-blue-600 text-white text-xs font-semibold mb-1.5 hover:bg-blue-700 transition-colors"
                                >
                                  {step.module}
                                </a>

                                <p className="text-sm text-gray-700">{step.action}</p>
                                <p className="text-sm text-gray-500 italic mt-0.5">{step.artifact}</p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>

                      {/* Full width, so the two columns above end together
                          instead of leaving the shorter one trailing space. */}
                      <div className="mt-8 rounded-lg bg-slate-100 p-5">
                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">What this makes possible</p>

                        <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                          {item.enables.map((claim) => (
                            <li key={claim} className="text-sm text-gray-700 leading-relaxed">{claim}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-8 pt-8 border-t border-slate-200">
                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-5">What it touches</p>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                          {IMPACT_GROUPS.map((group) => (
                            <div key={group.key}>
                              <p className="text-sm font-semibold text-gray-900 mb-2 pb-2 border-b border-slate-200">
                                {group.label}
                              </p>

                              <ul className="space-y-2">
                                {item.impact[group.key].map((entry) => (
                                  <li key={entry.name}>
                                    <p className="text-sm text-gray-700 leading-snug">{entry.name}</p>

                                    {(entry.detail || entry.location) && (
                                      <p className="text-xs text-gray-500 leading-snug flex flex-wrap items-center gap-x-1.5">
                                        {entry.detail && <span>{entry.detail}</span>}

                                        {entry.location && (
                                          <span className="inline-flex items-center gap-0.5 text-slate-400">
                                            <MapPin className="w-3 h-3 shrink-0" />
                                            {entry.location}
                                          </span>
                                        )}
                                      </p>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </TabsContent>
            );
          })}

          <div className="flex justify-center gap-2 mt-8">
            {scenarios.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`Go to scenario ${item.number}: ${item.title}`}
                className={`h-2.5 rounded-full transition-all ${
                  index === activeIndex ? 'w-8 bg-blue-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
                }`}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
        </Tabs>
      </div>
    </section>
  );
}

export default ScenarioCarousel;
